import { zodResolver } from '@hookform/resolvers/zod'
import { Button, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { getProfile, updateProfile as apiUpdateProfile } from 'api/profile'
import { useAuthContext } from 'components/context/AuthProvider'
import FormCheckbox from 'components/form-elements/FormCheckbox'
import FormDatePicker from 'components/form-elements/FormDatePicker'
import FormInput from 'components/form-elements/FormInput'
import FormMultipleSelect from 'components/form-elements/FormMultipleSelect'
import FormPhone from 'components/form-elements/FormPhone'
import FormRadioGroup from 'components/form-elements/FormRadioGroup'
import FormSelect from 'components/form-elements/FormSelect'
import NotificationSys from 'components/NotificationSystem'
import ModalFailPlaceholder from 'components/placeholders/ModalFailPlaceholder'
import Loader from 'components/placeholders/ModalLoadingPlaceholder'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PHONE_IS_INVALID } from 'const'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import { careLevels } from 'models/user.model'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import { useActions } from 'store/hooks'
import {
  BackendGender,
  ECareLevel,
  allCaregiverRoles,
  familyCaregiverRoleOptions,
  professionalCaregiverRoleOptions,
} from 'types'
import * as z from 'zod'

const genderOptions = {
  male: BackendGender.male,
  female: BackendGender.female,
  'Prefer to self describe': BackendGender.selfDescribe,
  'Prefer not to answer': BackendGender.notAnswer,
}

const required = z.string().min(1, 'Required')
const optional = z.string().optional()

const formSchema = z
  .object({
    email: required.email(EMAIL_IS_INVALID),
    firstName: required,
    lastName: required,
    street: optional,
    zipCode: optional,
    country: optional,
    city: optional,
    state: optional,
    phoneNumber: z.string().min(10, PHONE_IS_INVALID).or(z.literal('')).optional(),
    role: z
      .array(
        z.object({
          label: z.string(),
          value: z.string(),
        }),
      )
      .optional(),
    otherRole: optional,
    careLevel: optional,
    about: z.string().max(200, 'Max message length is 200 characters').optional(),
    isEmailNotificationEnabled: z.boolean().optional(),
    isSmsNotificationEnabled: z.boolean().optional(),
    dateOfBirth: z.date({ invalid_type_error: 'Invalid date', coerce: true }).nullable(),
    gender: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isSmsNotificationEnabled && (!data.phoneNumber || data.phoneNumber.length < 10)) {
      ctx.addIssue({
        path: ['phoneNumber'],
        code: z.ZodIssueCode.custom,
        message: FIELD_IS_REQUIRED,
      })
    }
  })

const formSettings = {
  mode: 'onChange' as const,
  resolver: zodResolver(formSchema),
}

type FormType = z.TypeOf<typeof formSchema>

const ProfileBasicInfoForm = ({ onClose, userId }: { onClose: () => void; userId: number }) => {
  const [form, setForm] = useState<FormType>()
  const { user } = useAuthContext()

  const query = useQuery(['getProfile', userId], () => getProfile(userId), {
    onSuccess: (data) => {
      let role: { value: string; label: string }[] = []
      if (data.role) {
        role = data.role.map((role) => ({
          label: allCaregiverRoles.find((option) => option.value === role)?.label || role,
          value: role,
        }))
      }
      const form = {
        ...data,
        role,
        dateOfBirth: data.dateOfBirth ? moment(data.dateOfBirth, 'MM/DD/YYYY').toDate() : null,
      }

      setForm(form)
    },
  })

  if (query.isLoading) {
    return <Loader show />
  }

  if (query.isError)
    return (
      <ModalFailPlaceholder
        show={true}
        py={7}
        error="Can't load profile data"
        handleClose={onClose}
      />
    )
  if (form) {
    return <Profile readonly={user?.id != userId} {...form} />
  }

  return null
}

const Profile = (props: FormType & { readonly: boolean }) => {
  const { isCaregiver } = useAuthContext()

  const formMethods = useForm<FormType>({
    ...formSettings,
    defaultValues: {
      ...props,
      role: props.role || [],
      otherRole: props.otherRole || '',
      careLevel: props.careLevel || '',
      street: props.street || '',
      zipCode: props.zipCode || '',
      country: props.country || '',
      city: props.city || '',
      state: props.state || '',
      about: props.about || '',
    },
  })
  const { updateProfile } = useActions()

  const formattedGenderOptions = useMemo(() => {
    return Object.entries(genderOptions).map((genderOption) => ({
      label: genderOption[0],
      value: genderOption[1],
    }))
  }, [])

  const updater = useMutation(apiUpdateProfile, {
    onSuccess: (data) => {
      const { firstName, lastName } = data.data
      updateProfile({
        firstName: firstName || props.firstName,
        lastName: lastName || props.lastName,
      })
      NotificationSys.showSuccess(`Profile successfully updated`)
    },
  })

  const onSubmit = (data: FormType) => {
    const role = data.role?.map((role) => role.value) || []
    const otherRole = hasOtherRole ? data.otherRole : ''
    updater.mutate({
      ...data,
      role,
      otherRole,
      careLevel: (data.careLevel as ECareLevel) || undefined,
      dateOfBirth: data.dateOfBirth && moment(data.dateOfBirth).format('MM/DD/YYYY'),
      gender: data.gender as BackendGender,
    })
  }

  const onError = () => {
    NotificationSys.showWarning(
      'Profile not saved. Please complete the required fields before saving',
    )
  }

  const aboutMeValue = formMethods.watch('about')
  const charactersLeft = useCharactersLeft(200, aboutMeValue?.length || 0)

  const roleValue = formMethods.watch('role')
  const hasOtherRole = useMemo(() => {
    return roleValue?.some((role) => role.value === 'other')
  }, [roleValue])

  const phoneNumber = formMethods.watch('phoneNumber')
  const smsWarningText = phoneNumber
    ? 'I agree to receive text notifications related to my training activity. Standard messaging rates apply. Reply ‘STOP’ to unsubscribe at any time.'
    : 'Please enter your mobile phone number.\n I agree to receive text notifications related to my training activity. Standard messaging rates apply. Reply ‘STOP’ to unsubscribe at any time.'

  const roleOptions = isCaregiver ? familyCaregiverRoleOptions : professionalCaregiverRoleOptions
  const roleLabel = isCaregiver ? 'Family Caregiver Role' : 'Professional Caregiver Role'

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit, onError)} noValidate>
        <Grid container columnSpacing={2}>
          {isCaregiver && (
            <Grid item sm={12} xs={12}>
              <FormInput
                disabled
                label="Email Address"
                required
                fullWidth
                sx={{ mb: 1 }}
                name="email"
              />
            </Grid>
          )}

          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={props.readonly}
              label="First Name"
              required
              fullWidth
              name="firstName"
              sx={{ mb: 1 }}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={props.readonly}
              label="Last Name"
              required
              fullWidth
              name="lastName"
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={props.readonly}
              label="Street Address"
              fullWidth
              name="street"
              sx={{ mb: 1 }}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={props.readonly}
              label="Zip Code"
              fullWidth
              name="zipCode"
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={props.readonly}
              label="City"
              fullWidth
              name="city"
              sx={{ mb: 1 }}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormInput
              disabled={props.readonly}
              label="State"
              fullWidth
              name="state"
              sx={{ mb: 1 }}
            />
          </Grid>
        </Grid>

        <Grid container columnSpacing={2} mb={1}>
          <Grid item sm={6} xs={12}>
            <FormInput disabled={props.readonly} label="Country" fullWidth name="country" />
          </Grid>

          <Grid item sm={6} xs={12}>
            <FormPhone
              label="Mobile Phone Number"
              name="phoneNumber"
              fullWidth
              disabled={props.readonly}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <FormDatePicker
              disabled={props.readonly}
              label="Date of Birth"
              name="dateOfBirth"
              fullWidth
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormMultipleSelect
              name="role"
              label={roleLabel}
              options={roleOptions}
              defaultValue={[]}
              isLoading={false}
              disabled={props.readonly}
              placeholder="Select role(s)"
            />
          </Grid>
          {hasOtherRole && (
            <Grid item sm={6} xs={12}>
              <FormInput name="otherRole" label="Other Role" disabled={props.readonly} />
            </Grid>
          )}

          {isCaregiver && (
            <Grid item sm={6} xs={12}>
              <FormSelect
                name="careLevel"
                label="What level of care does your person of concern need?"
                options={careLevels}
                disabled={props.readonly}
              />
            </Grid>
          )}

          <Grid item sm={12} xs={12}>
            <FormInput
              name="about"
              label="What you might want to know about me"
              placeholder="Please enter the relevant information about you"
              fullWidth
              minRows={3}
              maxRows={6}
              multiline
              helperText={charactersLeft}
              checkTouchField={false}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={2}>
          <Grid item sm={6} xs={12}>
            <Typography fontWeight={500}>Notification Settings</Typography>
            <Typography fontSize={14} sx={{ opacity: '0.6' }}>
              Receive reminders Weekly
            </Typography>

            <Stack flexDirection="row">
              <FormCheckbox
                name="isEmailNotificationEnabled"
                label="Email"
                disabled={props.readonly}
              />
              <FormCheckbox
                name="isSmsNotificationEnabled"
                label="Text messages"
                disabled={props.readonly}
                warningText={smsWarningText}
              />
            </Stack>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormRadioGroup
              disabled={props.readonly}
              label="Sex"
              required={false}
              name="gender"
              options={formattedGenderOptions}
            />
          </Grid>
        </Grid>

        {!props.readonly && (
          <Button
            variant="contained"
            type="submit"
            sx={{ minWidth: 160, height: 36.5, mt: 3, mb: 1, color: '#FFF' }}
          >
            {updater.isLoading && <CircularProgress size="1.5rem" color="inherit" />}
            {!updater.isLoading && 'Save'}
          </Button>
        )}
      </form>
    </FormProvider>
  )
}

export default React.memo(ProfileBasicInfoForm)
